import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { inr, num } from "@/lib/format";
import { toast } from "sonner";
import { Wrench, Play, FlaskConical, AlertTriangle } from "lucide-react";

const EXP = ["near", "mid", "far"];

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-slate-600">{hint}</div>}
    </div>
  );
}

const inputCls = "w-full bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-3 py-2 text-sm mono outline-none focus:border-[#2563EB]";

export default function StrategyBuilder() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [symbols, setSymbols] = useState([]);
  const [sim, setSim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: "", symbol: params.get("symbol") || "RELIANCE",
    buy_expiry: "near", sell_expiry: "far",
    capital_limit: 300000, safety_reserve_percentage: 10, charges_reserve: 2000,
    max_margin_util_pct: 70, min_free_funds: 50000,
    initial_entry_spread: 5, min_days_to_expiry: 3,
    trigger_percentage: 20, tolerance_percentage: 10,
    maximum_levels: 5, maximum_total_lots: 20, maximum_lots_per_level: 10,
    mode: "strict",
    profit_target_rupees: 10000, target_spread: "", target_contraction: "",
    max_loss_rupees: 15000, max_spread_loss: "", auto_square_off: true,
  });

  useEffect(() => {
    api.get("/explorer").then((r) => setSymbols(r.data.rows.map((x) => x.symbol))).catch(() => {});
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const clean = () => {
    const c = { ...f };
    ["initial_entry_spread", "capital_limit", "safety_reserve_percentage", "charges_reserve",
      "trigger_percentage", "tolerance_percentage", "maximum_levels", "maximum_total_lots",
      "maximum_lots_per_level", "min_days_to_expiry", "max_margin_util_pct", "min_free_funds"].forEach((k) => c[k] = Number(c[k]));
    ["profit_target_rupees", "target_spread", "target_contraction", "max_loss_rupees", "max_spread_loss"].forEach((k) => {
      c[k] = c[k] === "" || c[k] == null ? null : Number(c[k]);
    });
    if (!c.name) c.name = `${c.symbol} ${c.buy_expiry.toUpperCase()}/${c.sell_expiry.toUpperCase()}`;
    return c;
  };

  const simulate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/strategies/simulate", clean()); setSim(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const create = async (start) => {
    setBusy(true);
    try {
      const { data } = await api.post("/strategies", clean());
      toast.success("Strategy created");
      if (start) { await api.post(`/strategies/${data.id}/start`); toast.success("Strategy activated"); }
      nav(`/strategies/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-bold tracking-tight flex items-center gap-2"><Wrench className="h-5 w-5 text-[#2563EB]" /> Strategy Builder</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Selection */}
          <section className="term-panel rounded-sm p-5 space-y-4">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Stock & Contract</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="NSE Stock">
                <select value={f.symbol} onChange={(e) => set("symbol", e.target.value)} className={inputCls} data-testid="builder-symbol">
                  {symbols.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Buy Expiry (lower)">
                <select value={f.buy_expiry} onChange={(e) => set("buy_expiry", e.target.value)} className={inputCls} data-testid="builder-buy-expiry">
                  {EXP.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Sell Expiry (higher)">
                <select value={f.sell_expiry} onChange={(e) => set("sell_expiry", e.target.value)} className={inputCls} data-testid="builder-sell-expiry">
                  {EXP.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Strategy Name">
                <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="auto" className={inputCls} data-testid="builder-name" />
              </Field>
            </div>
          </section>

          {/* Capital & entry */}
          <section className="term-panel rounded-sm p-5 space-y-4">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Capital & Initial Entry</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Max Capital ₹"><input type="number" value={f.capital_limit} onChange={(e) => set("capital_limit", e.target.value)} className={inputCls} data-testid="builder-capital" /></Field>
              <Field label="Safety Reserve %"><input type="number" value={f.safety_reserve_percentage} onChange={(e) => set("safety_reserve_percentage", e.target.value)} className={inputCls} /></Field>
              <Field label="Charges Reserve ₹"><input type="number" value={f.charges_reserve} onChange={(e) => set("charges_reserve", e.target.value)} className={inputCls} /></Field>
              <Field label="Initial Min Spread ₹"><input type="number" value={f.initial_entry_spread} onChange={(e) => set("initial_entry_spread", e.target.value)} className={inputCls} data-testid="builder-initial-spread" /></Field>
              <Field label="Max Margin Util %"><input type="number" value={f.max_margin_util_pct} onChange={(e) => set("max_margin_util_pct", e.target.value)} className={inputCls} /></Field>
              <Field label="Min Days To Expiry"><input type="number" value={f.min_days_to_expiry} onChange={(e) => set("min_days_to_expiry", e.target.value)} className={inputCls} /></Field>
            </div>
          </section>

          {/* Multi-level */}
          <section className="term-panel rounded-sm p-5 space-y-4">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Multi-Level Averaging <span className="text-slate-600 normal-case font-normal">(quantity auto-calculated — no lots-per-level field)</span></div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Trigger %" hint="Spread must widen by this % vs last level"><input type="number" value={f.trigger_percentage} onChange={(e) => set("trigger_percentage", e.target.value)} className={inputCls} data-testid="builder-trigger" /></Field>
              <Field label="Max Avg-Distance %" hint="Keep wavg within this % of current spread"><input type="number" value={f.tolerance_percentage} onChange={(e) => set("tolerance_percentage", e.target.value)} className={inputCls} data-testid="builder-tolerance" /></Field>
              <Field label="Max Levels (3-10)"><input type="number" value={f.maximum_levels} onChange={(e) => set("maximum_levels", e.target.value)} className={inputCls} data-testid="builder-max-levels" /></Field>
              <Field label="Max Total Lots"><input type="number" value={f.maximum_total_lots} onChange={(e) => set("maximum_total_lots", e.target.value)} className={inputCls} /></Field>
              <Field label="Max Lots / Level"><input type="number" value={f.maximum_lots_per_level} onChange={(e) => set("maximum_lots_per_level", e.target.value)} className={inputCls} /></Field>
              <Field label="Averaging Mode">
                <select value={f.mode} onChange={(e) => set("mode", e.target.value)} className={inputCls} data-testid="builder-mode">
                  <option value="strict">Strict Average (default)</option>
                  <option value="best">Best Available</option>
                </select>
              </Field>
            </div>
            {f.mode === "best" && (
              <div className="flex items-center gap-2 text-[11px] text-[#FFCC00] bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-sm p-2">
                <AlertTriangle className="h-4 w-4" /> Best Available mode may execute fewer lots than required and will not maintain your average tolerance.
              </div>
            )}
          </section>

          {/* Exit */}
          <section className="term-panel rounded-sm p-5 space-y-4">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Exit & Risk</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Profit Target ₹"><input type="number" value={f.profit_target_rupees} onChange={(e) => set("profit_target_rupees", e.target.value)} className={inputCls} data-testid="builder-profit-target" /></Field>
              <Field label="Target Spread ₹" hint="Exit when exec exit spread ≤ this"><input type="number" value={f.target_spread} onChange={(e) => set("target_spread", e.target.value)} className={inputCls} /></Field>
              <Field label="Target Contraction ₹"><input type="number" value={f.target_contraction} onChange={(e) => set("target_contraction", e.target.value)} className={inputCls} /></Field>
              <Field label="Max Loss ₹"><input type="number" value={f.max_loss_rupees} onChange={(e) => set("max_loss_rupees", e.target.value)} className={inputCls} data-testid="builder-max-loss" /></Field>
              <Field label="Max Spread Widening ₹"><input type="number" value={f.max_spread_loss} onChange={(e) => set("max_spread_loss", e.target.value)} className={inputCls} /></Field>
              <Field label="Square-off">
                <select value={f.auto_square_off ? "auto" : "manual"} onChange={(e) => set("auto_square_off", e.target.value === "auto")} className={inputCls}>
                  <option value="auto">Auto square-off</option>
                  <option value="manual">Manual only</option>
                </select>
              </Field>
            </div>
          </section>
        </div>

        {/* Simulation sidebar */}
        <div className="space-y-4">
          <div className="term-panel rounded-sm p-5 sticky top-4">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-2"><FlaskConical className="h-4 w-4" /> Pre-Trade Simulation</div>
            <button onClick={simulate} disabled={busy} data-testid="builder-simulate" className="w-full mb-3 text-sm font-semibold py-2 rounded-sm bg-[#1A1D24] border border-[#2A2F3A] hover:bg-[#232730]">Run Simulation</button>
            {sim ? (
              <div className="space-y-2 text-xs">
                {[["Direction", sim.direction], ["Buy Ask", inr(sim.buy_ask)], ["Sell Bid", inr(sim.sell_bid)],
                  ["Executable Entry Spread", "₹" + sim.executable_entry_spread], ["Lot Size", num(sim.lot_size, 0)],
                  ["Margin / Matched Lot", inr(sim.margin_per_matched_lot)], ["Initial Lots", num(sim.initial_lots, 0)],
                  ["Max Affordable Lots", num(sim.max_affordable_lots, 0)], ["Next Trigger Spread", "₹" + sim.next_trigger_spread],
                  ["Safety Reserve", inr(sim.safety_reserve)]].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-[#20242c] pb-1.5">
                    <span className="text-slate-500">{k}</span><span className="mono text-slate-100">{v}</span>
                  </div>
                ))}
                <div className={`mt-2 px-2 py-1.5 rounded-sm text-center font-bold ${sim.entry_eligible ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>
                  {sim.entry_eligible ? "ENTRY ELIGIBLE" : sim.fno_ban ? "F&O BAN — BLOCKED" : "SPREAD BELOW INITIAL THRESHOLD"}
                </div>
              </div>
            ) : <div className="text-xs text-slate-600">Run simulation to preview executable spread, margin and lot sizing before activating.</div>}

            <div className="mt-4 space-y-2">
              <button onClick={() => create(true)} disabled={busy} data-testid="builder-create-start" className="w-full text-sm font-bold py-2.5 rounded-sm bg-[#2563EB] hover:bg-[#1d4fd0] flex items-center justify-center gap-2">
                <Play className="h-4 w-4" /> Create & Activate
              </button>
              <button onClick={() => create(false)} disabled={busy} data-testid="builder-create-draft" className="w-full text-xs font-semibold py-2 rounded-sm bg-[#1A1D24] border border-[#2A2F3A] hover:bg-[#232730]">Save as Draft</button>
            </div>
            <div className="text-[10px] text-slate-600 mt-3">Estimated figures only. Averaging increases capital exposure. Not guaranteed profit.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
